import React, {useCallback, useEffect, useState} from 'react'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import {AiOutlineCheck} from 'react-icons/ai';
import s from './styles/EditAvailability.module.scss';
import {IoIosRemoveCircleOutline} from 'react-icons/io';
import {SelectInput} from 'Input/Input';
import {Root} from 'Root'
import {times} from 'Utils/time'
import Modal from 'react-bootstrap/esm/Modal';
import {Link, useLocation} from "react-router-dom";
import {getTimezone} from "../../../../Utils/time";
import ReactTooltip from "react-tooltip";
import {Tooltip} from "recharts";
import Spinner from "react-bootstrap/Spinner";

const days = {
    'SUN': 'Sunday',
    'MON': 'Monday',
    'TUE': 'Tuesday',
    'WED': 'Wednesday',
    'THU': 'Thursday',
    'FRI': 'Friday',
    'SAT': 'Saturday',
}

export const EditAvailability = ({session, refreshSession}) => {
    const [availability, setAvailability] = useState(undefined)
    const [summary, setSummary] = useState(undefined)
    const [saving, setSaving] = useState(false)
    const [show, setShow] = useState(false)
    const handleClose = () => {
        setShow(false)
    }
    const handleShow = () => {
        setShow(true)
    }
    const [fragment, setFragment] = useState(useLocation().hash)
    const visualCalendarEnabled = true

    const getAvailability = useCallback(() => {
        Root.userApi.getAvailabilityLocal(session.jwt).then((res) => {
            setSummary(res.data?.summary)
            setAvailability(res.data?.availability)
        })
    }, [session.jwt]);

    useEffect(() => {
        if (availability) {
            return
        }

        getAvailability()
    }, [availability, getAvailability])

    useEffect(() => {
        if (fragment === '#availability' || fragment === 'availability' || fragment === '#Availability' || fragment === 'Availability') {
            const newUrl = window.location.href.split('#')[0];
            window.history.replaceState({}, document.title, newUrl);
            setShow(true)
            setFragment(undefined)
        }
    }, [fragment, availability])

    function saveAvailability(newAvailability) {
        if (JSON.stringify(availability) === JSON.stringify(newAvailability)) {
            setShow(false)
            return;
        }

        setSaving(true)

        Root.userApi.setAvailability(newAvailability, session.jwt).then(() => {
            getAvailability()
            setSaving(false)
            setShow(false)
        }).catch((ignore) => {})
    }

    return <Card className='xlCard'>
        <Card.Body>
            <span className={s.aiSummaryHeader}>
                AI Powered Summary</span>
            <p className={s.aiSummary}>{summary}</p>
            <Button onClick={handleShow} className='btnLink wide'>Edit Availability</Button>
            <Modal show={show} onHide={handleClose} size={visualCalendarEnabled ? "xl" : 'lg'} fullscreen={'xl-down'}>
                <Modal.Header closeButton>
                    <Modal.Title>Set Your Weekly Availability</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {visualCalendarEnabled ?
                        <p>On each day, please indicate when you are <span className={s.busy}>busy</span> and cannot
                            take meetings. Your timezone is
                            automatically set to <strong>{getTimezone().replaceAll("_", " ")}</strong></p>
                        :
                        <p>On each day, please indicate when you are available to take meetings. Your timezone is
                            automatically set to <strong>{getTimezone().replaceAll("_", " ")}</strong></p>
                    }
                    {availability && visualCalendarEnabled ?
                        <CalendarSchedule availability={availability} saveAvailability={saveAvailability} saving={saving}/>
                        :
                        <>
                            {
                                Object.keys(days).map((day, idx) => (
                                    <>
                                        <div key={idx}>
                                            <TimePicker availability={availability} day={day} session={session}
                                                        getAvailability={getAvailability}/>
                                            <hr/>
                                        </div>
                                    </>
                                ))
                            }
                        </>
                    }
                </Modal.Body>
            </Modal>
        </Card.Body>
    </Card>;
}

const CalendarSchedule = ({availability, saveAvailability, saving}) => {
    const [mutableAvailability, setMutableAvailability] = useState(structuredClone(availability))
    const [mouseDown, setMouseDown] = useState(false)

    const timeSegments = Array.from({length: 48}, (_, i) => i);

    function updateAvailabilityOnDay(day, timeslot) {
        let currentValuesOnDay = mutableAvailability[day]
        const currentValueAtTime = currentValuesOnDay[timeslot]
        currentValuesOnDay[timeslot] = !currentValueAtTime
        setMutableAvailability(prevState => ({...prevState, [day]: currentValuesOnDay}))
    }

    function handleMouseDown() {
        setMouseDown(true)
    }

    function handleMouseUp() {
        setMouseDown(false)
    }

    function handleMouseOver(day, timeslot) {
        if (mouseDown) {
            updateAvailabilityOnDay(day, timeslot)
        }
    }

    return <>
        <div>
            <div className={'d-flex gap-4'}>
                <Button disabled={saving} className={'btnOutline md my-3 d-block wide'} onClick={() => setMutableAvailability(JSON.parse(JSON.stringify(availability)))}>
                    Reset
                </Button>
                <Button disabled={saving} className={'btnFill md my-3 d-block wide'} onClick={() => saveAvailability(JSON.parse(JSON.stringify(mutableAvailability)))}>
                    {saving ? <Spinner style={{color: "rgb(225, 225, 225)"}} animation="border" role="status"/> : `Save Availability`}
                </Button>
            </div>
            <div className={s.calendarSchedule}>
                <div id={'calendar-grid'} className={s.calendarGrid} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
                    {/*Placeholder for Time Labels*/}
                    <div className={s.dayHeader}></div>
                    {/*Day Headers*/}
                    <div className={s.dayHeader}>Sun</div>
                    <div className={s.dayHeader}>Mon</div>
                    <div className={s.dayHeader}>Tue</div>
                    <div className={s.dayHeader}>Wed</div>
                    <div className={s.dayHeader}>Thu</div>
                    <div className={s.dayHeader}>Fri</div>
                    <div className={s.dayHeader}>Sat</div>

                    {mutableAvailability && timeSegments.map((segment) => (
                        <>
                            {(segment % 2 === 0) ?
                                <div
                                    className={s.timeLabel}>{(segment / 2) === 0 ? '12' : ((segment / 2) > 12 ? ((segment / 2) - 12) : (segment / 2))} {segment < 23 ? 'AM' : 'PM'}</div>
                                :
                                <div></div>
                            }
                            {Object.keys(days).map((day, idx) => {
                                const isAvailable = mutableAvailability[day][segment];
                                return <>
                                    <div onMouseOver={() => handleMouseOver(day, segment)} className={`${s.timeSlot} ${isAvailable ? '' : s.selected}`} onClick={() => {
                                        updateAvailabilityOnDay(day, segment)
                                    }}></div>
                                </>
                            })}
                        </>
                    ))}
                </div>
            </div>
        </div>
    </>
}


const TimePicker = ({day, availability, session, getAvailability}) => {
    const [formErrors, setFormErrors] = useState({});
    const [end, setEnd] = useState(0);
    const [start, setStart] = useState(0);
    const [ranges, setRanges] = useState(null)
    const [responseError, setResponseError] = useState(null)
    const [editing, setEditing] = useState(false)

    const handleAddRange = () => {
        setFormErrors({})
        if (!start && !end) {
            setFormErrors({end: 'Incomplete', start: 'Incomplete'})
        } else if (!start) {
            setFormErrors({start: 'Incomplete'})
        } else if (!end) {
            setFormErrors({end: 'Incomplete'})
        } else if (parseInt(start) >= parseInt(end)) {
            setFormErrors({end: 'Invalid end time', start: 'Invalid start time'})
        } else {
            setRanges((prev) => [...prev, [start, end]])
        }
    }
    const handleDeleteRange = (idx) => {
        setRanges(ranges.filter((_, i) => i !== idx))
    }
    const handleChangeEnd = (e) => {
        setEnd(e.target.value)
    }
    const handleChangeStart = (e) => {
        setStart(e.target.value);
    }
    const handleEdit = () => {
        if (editing) {
            setFormErrors({})
            if (parseInt(start) >= parseInt(end)) {
                setFormErrors({end: 'Invalid end time', start: 'Invalid start time'})
            } else if ((start && !end)) {
                setFormErrors({end: 'Incomplete', start: 'Incomplete'})
            } else if ((end && !start)) {
                setFormErrors({start: 'Incomplete', end: 'Incomplete'})
            } else {
                handleSubmit()
            }
        } else {
            setEditing(true)
        }
    }

    useEffect(() => {
        setStart(null);
        setEnd(null);
        if (!ranges) {
            setRanges([])
        }
    }, [ranges])

    useEffect(() => {
        if (availability && availability[day]) {
            setRanges(boolArrToRanges())
        }
    }, [availability])

    function rangesToBoolArr() {
        let boolAvail = new Array(48).fill(false)
        ranges.map((range) => {
            let start = range[0]
            let end = range[1]
            let len = end - start // get length of the time range
            let avail = new Array(len).fill(true)
            boolAvail.splice(start, len, ...avail)
        })
        return boolAvail;
    }

    function boolArrToRanges() {
        let res = []
        let currAvail = availability[day]
        let start = null;
        for (let i = 0; i < currAvail.length; i++) {
            if (currAvail[i] === true && !start) {
                start = i;
            } else if (currAvail[i] === false && i - 1 >= 0 && !!start) {
                res.push([start, i - 1])
                start = null;
            }

            if (start && i === currAvail.length - 1) {
                res.push([start, i])
            } // reached the end of the array, close the range
        }
        return res
    }

    function handleSubmit() {
        setResponseError(null)
        availability[day] = rangesToBoolArr()
        Root.userApi.setAvailability(availability, session.jwt).then(() => {
            setEditing(false)
            getAvailability()
        }).catch(() => {
                setResponseError('Unable to save changes, please try again')
            }
        )
    }

    return <div className='mb-4'>
        <span className='d-flex align-items-center justify-content-between'>
            <p className='mb-0'><strong>{days[day]}</strong></p>
            <Link className='btnLink mb-3 fs-14' onClick={handleEdit}>{editing ? 'Save' : 'Edit'}</Link>
        </span>

        {editing && <div className='d-flex align-items-center justify-content-between'>
            <span className='d-flex align-items-center  mt-2'>
                <SelectInput containerClassName='me-3' placeholder='Start' errors={formErrors} ordered={'false'}
                             options={times} name={'start'} value={start} onChange={handleChangeStart}/>
                <SelectInput containerClassName='ms-3' placeholder='End' errors={formErrors} ordered={'false'}
                             options={times} name={'end'} value={end} onChange={handleChangeEnd}/>
                <Button className='btnLink' onClick={handleAddRange}><AiOutlineCheck/> Confirm Range</Button>
            </span>
        </div>}

        {responseError && <p className='form-error'>{responseError}</p>}

        <div>
            {(ranges && ranges.length > 0) ? ranges.map((range, idx) => {
                return <Button disabled={!editing} key={idx} onClick={() => handleDeleteRange(idx)}
                               className='me-2 mt-2 btnFill'>{times[range[0]]} - {times[range[1]]}
                    {' '} {editing && <IoIosRemoveCircleOutline/>}</Button>
            }) : <span className='text-muted fs-13' style={{fontWeight: 'normal'}}>You will not receive any meeting requests for {days[day]}s</span>}
        </div>
    </div>
}